// plan-02 S06: SFX library — bible S17
// All synth-generated, no audio files

import { getCtx, getMasterGain } from './context'

type SfxName =
  | 'boot_chime'
  | 'terminal_keypress'
  | 'command_accept'
  | 'command_reject'
  | 'quorum_chime'
  | 'palimpsest_leak'
  | 'golden_pixel_49'
  | 'offline_seal'
  | 'online_seal'
  | 'broadcast_open'
  | 'broadcast_close'
  | 'node_tick'
  | 'commune_resolve'

let lastKeypress = 0
const KEYPRESS_RATE_LIMIT = 250 // <= 4/sec

function dbfsToGain(dbfs: number): number {
  return Math.pow(10, dbfs / 20)
}

function envelope(
  param: AudioParam,
  atk: number,
  rel: number,
  peak: number,
  now: number,
): void {
  param.setValueAtTime(0, now)
  param.linearRampToValueAtTime(peak, now + atk)
  param.linearRampToValueAtTime(0, now + atk + rel)
}

const cues: Record<SfxName, (ctx: AudioContext, opts: { delay?: number; volume?: number }) => void> = {
  boot_chime(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    envelope(g.gain, 0.005, 0.06, dbfsToGain(-20) * (opts.volume ?? 1), now)
    const o1 = ctx.createOscillator()
    o1.type = 'sine'
    o1.frequency.value = 192
    o1.connect(g)
    o1.start(now)
    o1.stop(now + 0.08)
    const g2 = ctx.createGain()
    g2.connect(getMasterGain())
    envelope(g2.gain, 0.005, 0.06, dbfsToGain(-20) * (opts.volume ?? 1), now + 0.09)
    const o2 = ctx.createOscillator()
    o2.type = 'sine'
    o2.frequency.value = 384
    o2.connect(g2)
    o2.start(now + 0.09)
    o2.stop(now + 0.17)
  },

  terminal_keypress(ctx, opts) {
    const now = performance.now()
    if (now - lastKeypress < KEYPRESS_RATE_LIMIT) return
    lastKeypress = now

    const t = ctx.currentTime + (opts.delay ?? 0)
    const bufSize = Math.floor(ctx.sampleRate * 0.018)
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1)
    const src = ctx.createBufferSource()
    src.buffer = buf
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 1200
    const g = ctx.createGain()
    g.gain.value = dbfsToGain(-38) * (opts.volume ?? 1)
    src.connect(lp)
    lp.connect(g)
    g.connect(getMasterGain())
    src.start(t)
  },

  command_accept(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    envelope(g.gain, 0.005, 0.07, dbfsToGain(-22) * (opts.volume ?? 1), now)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = 480
    o.connect(g)
    o.start(now)
    o.stop(now + 0.09)
  },

  command_reject(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    g.gain.setValueAtTime(dbfsToGain(-24) * (opts.volume ?? 1), now)
    g.gain.linearRampToValueAtTime(0, now + 0.14)
    const o = ctx.createOscillator()
    o.type = 'triangle'
    o.frequency.value = 240
    o.connect(g)
    o.start(now)
    o.stop(now + 0.15)
  },

  quorum_chime(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const freqs = [192, 288, 384]
    freqs.forEach((f, i) => {
      const g = ctx.createGain()
      g.connect(getMasterGain())
      envelope(g.gain, 0.005, 0.08, dbfsToGain(-22) * (opts.volume ?? 1), now + i * 0.06)
      const o = ctx.createOscillator()
      o.type = 'sine'
      o.frequency.value = f
      o.connect(g)
      o.start(now + i * 0.06)
      o.stop(now + i * 0.06 + 0.09)
    })
  },

  palimpsest_leak(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    g.gain.setValueAtTime(0, now)
    g.gain.linearRampToValueAtTime(dbfsToGain(-28) * (opts.volume ?? 1), now + 0.5)
    g.gain.linearRampToValueAtTime(0, now + 4.7)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = 47
    o.connect(g)
    o.start(now)
    o.stop(now + 4.8)
  },

  golden_pixel_49(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    g.gain.setValueAtTime(dbfsToGain(-16) * (opts.volume ?? 1), now)
    g.gain.linearRampToValueAtTime(0, now + 0.01)
    const o = ctx.createOscillator()
    o.type = 'square'
    o.frequency.value = 1000
    o.connect(g)
    o.start(now)
    o.stop(now + 0.01)
  },

  // plan-11 S05: offline seal — 47 Hz sine, 0.4s, atk 0.05, rel 0.35
  offline_seal(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    envelope(g.gain, 0.05, 0.35, dbfsToGain(-24) * (opts.volume ?? 1), now)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = 47
    o.connect(g)
    o.start(now)
    o.stop(now + 0.4)
  },

  // plan-11 S05: online seal — 96 Hz sine, 0.3s, soft attack
  online_seal(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    envelope(g.gain, 0.04, 0.26, dbfsToGain(-22) * (opts.volume ?? 1), now)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = 96
    o.connect(g)
    o.start(now)
    o.stop(now + 0.3)
  },

  // plan-13 S06: broadcast open — 96 Hz triad, 3 stacked sines, 1.2s envelope
  broadcast_open(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const freqs = [96, 144, 192]
    freqs.forEach((f) => {
      const g = ctx.createGain()
      g.connect(getMasterGain())
      envelope(g.gain, 0.08, 1.12, dbfsToGain(-26) * (opts.volume ?? 1), now)
      const o = ctx.createOscillator()
      o.type = 'sine'
      o.frequency.value = f
      o.connect(g)
      o.start(now)
      o.stop(now + 1.2)
    })
  },

  // plan-13 S06: broadcast close — 47 Hz sine, 0.8s
  broadcast_close(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    envelope(g.gain, 0.05, 0.75, dbfsToGain(-24) * (opts.volume ?? 1), now)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = 47
    o.connect(g)
    o.start(now)
    o.stop(now + 0.8)
  },

  // plan-13 S06: node tick — 384 Hz click, 30ms, -16 dB
  node_tick(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const g = ctx.createGain()
    g.connect(getMasterGain())
    g.gain.setValueAtTime(dbfsToGain(-16) * (opts.volume ?? 1), now)
    g.gain.linearRampToValueAtTime(0, now + 0.03)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = 384
    o.connect(g)
    o.start(now)
    o.stop(now + 0.03)
  },

  // plan-18 S05: commune resolve — three sines (192→144→96 Hz) staggered 80ms, total 320ms
  commune_resolve(ctx, opts) {
    const now = ctx.currentTime + (opts.delay ?? 0)
    const freqs = [192, 144, 96]
    freqs.forEach((f, i) => {
      const g = ctx.createGain()
      g.connect(getMasterGain())
      envelope(g.gain, 0.005, 0.07, dbfsToGain(-22) * (opts.volume ?? 1), now + i * 0.08)
      const o = ctx.createOscillator()
      o.type = 'sine'
      o.frequency.value = f
      o.connect(g)
      o.start(now + i * 0.08)
      o.stop(now + i * 0.08 + 0.08)
    })
  },
}

export type { SfxName }

export function play(name: SfxName, opts?: { delay?: number }): void {
  try {
    const ctx = getCtx()
    if (ctx.state !== 'running') return
    if (muted) return
    cues[name](ctx, { delay: opts?.delay, volume: 1 })
  } catch {
    // audio cues never throw
  }
}

// plan-17 S04: cursor click variation — pitch preset from char code
type PitchPreset = 'low' | 'mid' | 'high'
const PITCH_CUTOFFS: Record<PitchPreset, number> = { low: 800, mid: 1200, high: 1800 }

export function playKeypress(char?: string): void {
  try {
    const ctx = getCtx()
    if (ctx.state !== 'running') return
    if (muted) return

    const now = performance.now()
    if (now - lastKeypress < KEYPRESS_RATE_LIMIT) return
    lastKeypress = now

    const code = char ? char.charCodeAt(0) : 0
    const presets: PitchPreset[] = ['low', 'mid', 'high']
    const preset = presets[code % 3]

    const t = ctx.currentTime
    const bufSize = Math.floor(ctx.sampleRate * 0.018)
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1)
    const src = ctx.createBufferSource()
    src.buffer = buf
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = PITCH_CUTOFFS[preset]
    const g = ctx.createGain()
    g.gain.value = dbfsToGain(-38)
    src.connect(lp)
    lp.connect(g)
    g.connect(getMasterGain())
    src.start(t)
  } catch {
    // audio cues never throw
  }
}

// shared mute state
let muted = false

export function setSfxMuted(m: boolean): void {
  muted = m
}
